import React, { useMemo, useState } from "react";
import { HourlyFunnelData } from "../../services/funnelService";
import { useGridCrosshair } from "./useGridCrosshair";
import { FunnelView, ViewSwitch } from "./ViewSwitch";
import { WaveChart } from "./WaveChart";
import { formatStageLabel, getHeatClass, STAGES } from "./utils";

interface HourlyActivityProps {
  rows: HourlyFunnelData[];
  isLoading?: boolean;
}

const HOURLY_PERCENTAGE_COLUMNS: Array<{
  key: keyof HourlyFunnelData;
  label: string;
}> = [
  {
    key: "preloan_to_created_drop_percent",
    label: "Created",
  },
  {
    key: "created_to_verified_percent",
    label: "Verified",
  },
  {
    key: "verified_to_app_custcared_percent",
    label: "App Custcared",
  },
  {
    key: "app_custcared_to_call_initialized_percent",
    label: "Call Init",
  },
  {
    key: "call_initialized_to_call_approved_percent",
    label: "Call Approve",
  },
  {
    key: "call_approved_to_contract_signed_percent",
    label: "Contract Sign",
  },
  {
    key: "contract_signed_to_sanad_created_percent",
    label: "Sanad Created",
  },
  {
    key: "sanad_created_to_nafaes_sales_ordered_percent",
    label: "Nafaes Sales",
  },
];

// Salary Check is hidden for now (user request); the data is still fetched.
const HIDDEN_STAGES: string[] = [
  "commodity_purchased_requested",
  "salary_check",
];

const VISIBLE_STAGES = STAGES.filter((stage) => !HIDDEN_STAGES.includes(stage));

const HOURLY_STAGES = VISIBLE_STAGES.map((stage) => ({
  key: stage,
  label:
    stage === "preloan_eligibility"
      ? "Pre-Loan Eligibility"
      : formatStageLabel(stage),
}));

const formatHourTitle = (label: string): string => `Hour ${label}`;

export const HourlyActivity: React.FC<HourlyActivityProps> = React.memo(
  ({ rows }) => {
    const countsCrosshair = useGridCrosshair();
    const percentCrosshair = useGridCrosshair();
    const [countsView, setCountsView] = useState<FunnelView>("wave");
    const [percentView, setPercentView] = useState<FunnelView>("wave");

    // Wave view reads left to right in time; rows arrive latest hour first.
    const waveRows = useMemo(() => [...rows].reverse(), [rows]);
    const waveHours = useMemo(
      () => waveRows.map((row) => row.hour_range),
      [waveRows],
    );
    const countsSeries = useMemo(
      () =>
        HOURLY_STAGES.map((stage) => ({
          label: stage.label,
          data: waveRows.map((row) => Number(row[stage.key] ?? 0)),
        })),
      [waveRows],
    );
    const percentSeries = useMemo(
      () =>
        HOURLY_PERCENTAGE_COLUMNS.map((column) => ({
          label: column.label,
          data: waveRows.map((row) => Number(row[column.key] ?? 0)),
        })),
      [waveRows],
    );
    const maxPercent = useMemo(
      () =>
        Math.max(
          0,
          ...rows.flatMap((row) =>
            HOURLY_PERCENTAGE_COLUMNS.map((column) =>
              Number(row[column.key] ?? 0),
            ),
          ),
        ),
      [rows],
    );
    const maxValue = useMemo(
      () =>
        Math.max(
          1,
          ...rows.flatMap((row) =>
            HOURLY_STAGES.map((stage) => Number(row[stage.key] ?? 0)),
          ),
        ),
      [rows],
    );

    // Both hourly sections come from the same API; hide them entirely when it has no rows.
    if (rows.length === 0) {
      return null;
    }

    return (
      <>
        <section className="funnel-minimal-section">
          <div className="funnel-minimal-heading-row">
            <div className="funnel-minimal-heading">
              <h2 className="funnel-minimal-heading-title">
                Today Activity (Hourly)
              </h2>
              <p className="funnel-minimal-heading-subtitle">
                Application count by hour and stage
              </p>
            </div>
            <ViewSwitch value={countsView} onChange={setCountsView} />
          </div>

          {countsView === "wave" ? (
            <WaveChart
              labels={waveHours}
              series={countsSeries}
              tooltipTitle={formatHourTitle}
              showLegend
              hoverMode="point"
            />
          ) : (
            <div className="funnel-minimal-hourly-grid-wrap">
              <table
                className="funnel-minimal-hourly-grid"
                {...countsCrosshair}
              >
                <thead>
                  <tr>
                    <th className="funnel-minimal-hourly-grid-hour">Hour</th>
                    {HOURLY_STAGES.map((stage) => (
                      <th key={`hourly-grid-header-${stage.key}`}>
                        {stage.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`hourly-grid-row-${row.hour_range}`}>
                      <th
                        scope="row"
                        className="funnel-minimal-hourly-grid-hour"
                      >
                        {row.hour_range}
                      </th>
                      {HOURLY_STAGES.map((stage) => {
                        const value = Number(row[stage.key] ?? 0);
                        return (
                          <td
                            key={`hourly-grid-cell-${row.hour_range}-${stage.key}`}
                            className={getHeatClass(value, maxValue)}
                          >
                            {value.toLocaleString()}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="funnel-minimal-section">
          <div className="funnel-minimal-heading-row">
            <div className="funnel-minimal-heading">
              <h2 className="funnel-minimal-heading-title">
                Today Activity (Hourly) (%)
              </h2>
              <p className="funnel-minimal-heading-subtitle">
                Stage conversion % by hour
              </p>
            </div>
            <ViewSwitch value={percentView} onChange={setPercentView} />
          </div>

          {percentView === "wave" ? (
            <WaveChart
              labels={waveHours}
              series={percentSeries}
              isPercent
              tooltipTitle={formatHourTitle}
              showLegend
              hoverMode="point"
            />
          ) : (
            <div className="funnel-minimal-hourly-grid-wrap">
              <table
                className="funnel-minimal-hourly-grid funnel-minimal-hourly-grid-teal funnel-minimal-hourly-grid-compact"
                {...percentCrosshair}
              >
                <thead>
                  <tr>
                    <th className="funnel-minimal-hourly-grid-hour">Hour</th>
                    {HOURLY_PERCENTAGE_COLUMNS.map((column) => (
                      <th
                        key={`hourly-percentage-header-${String(column.key)}`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`hourly-percent-${row.hour_range}`}>
                      <th
                        scope="row"
                        className="funnel-minimal-hourly-grid-hour"
                      >
                        {row.hour_range}
                      </th>
                      {HOURLY_PERCENTAGE_COLUMNS.map((column) => {
                        const value = Number(row[column.key] ?? 0);
                        return (
                          <td
                            key={`hourly-percentage-row-${row.hour_range}-${String(column.key)}`}
                            className={getHeatClass(value, maxPercent)}
                          >
                            {value.toFixed(2)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </>
    );
  },
);
